import * as React from 'react';
import { env } from '@dx-ui/framework-env';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import { Button, Heading, Link, Text } from '@dx-ui/global-ui-prism';
import {
  usePageMetrics,
  PageName,
  useWrappedRouter,
  useEventMetrics,
  MetricsEvent,
} from '../../hooks';
import { useAuth } from '@dx-ui/framework-auth-provider';
import LoginFrame from '../../components/LoginFrame/LoginFrame';
import { useIsClient } from 'usehooks-ts';
import { logError } from '@dx-ui/framework-logger';
import { preprocessUserPackagesForMetrics } from '@dx-ui/config-metrics';

const SignIn: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const { setPageMetrics } = usePageMetrics();
  const { setEventMetrics } = useEventMetrics();
  const { isAuthenticated, guestId, guestInfo, isLoading } = useAuth();
  const { querystring, safeQueryParams, router } = useWrappedRouter();
  const isClient = useIsClient();

  const language = router.locale;
  const joinLink = env('HONORS_JOIN_URL');
  const bookNowLink = `https://events.hilton.com/`;
  const onPageLoad = () => {
    const commonData = { brandCode: 'HH', lang: language as string };
    const pageData = { brandName: 'HiltonHonors', lang: language as string };
    setPageMetrics(PageName.SIGN_IN, commonData, pageData);
  };

  React.useEffect(onPageLoad, [language, setPageMetrics]);

  React.useEffect(() => {
    if (isAuthenticated && !isLoading && (guestId || -1) > 0 && guestInfo) {
      setEventMetrics(MetricsEvent.LOG_IN, {
        hhonorsNumber: guestInfo.hhonors?.hhonorsNumber || '',
        tierName: guestInfo.hhonors?.summary?.tierName || '',
        totalPoints: guestInfo.hhonors?.summary?.totalPointsFmt || '',
        packages: preprocessUserPackagesForMetrics(guestInfo.hhonors?.packages) ?? [],
      });
      setEventMetrics(MetricsEvent.SIGN_IN_ONLY);
      const forwardPageURI = router?.query?.forwardPageURI as string;
      if (forwardPageURI) {
        router
          .push(forwardPageURI)
          .catch((error: string | Error | Record<string, unknown>) =>
            logError('ROUTING_ERROR', error, 'failed to router.push to forwardPageURI')
          );
      } else {
        router
          .push(
            {
              pathname: `/group-search`,
              query: {
                language,
                ...safeQueryParams,
              },
            },
            `/${language}/my-event/group-search/${querystring ? `?${querystring}` : ''}`
          )
          .catch((error: string | Error | Record<string, unknown>) =>
            logError('ROUTING_ERROR', error, 'failed to router.push to group-search page')
          );
      }
    }
  }, [
    guestId,
    isLoading,
    isAuthenticated,
    guestInfo,
    setEventMetrics,
    router,
    language,
    safeQueryParams,
    querystring,
  ]);

  const signInPageContent = t('textContent.signInPageContent') as unknown as Array<{
    bodyText: string;
  }>;

  return (
    <section className="relative my-4 block flex-col flex-wrap justify-center pb-11 pt-8 md:flex md:flex-row md:flex-nowrap md:items-center">
      {!isAuthenticated ? (
        <div className="w-full max-w-sm md:mr-[6%] md:flex-auto">
          <Heading level={1} size="xl" className="mb-2">
            {t('heading.signIn')}
          </Heading>
          {isClient && <LoginFrame />}
          <div className="my-6">
            <Text>
              {t('textContent.joinHonors')}{' '}
              <Link
                url={joinLink}
                data-testid="joinHonorsLink"
                isNewWindow
                aria-label={`${t('buttonsAndLinks.joinNow')} ${t('accessibility.opensNewTab')}`}
              >
                {t('buttonsAndLinks.joinNow')}
              </Link>
            </Text>
            <Text className="mt-2">
              {t('textContent.secureRoom')}{' '}
              <Link url={bookNowLink} data-testid="bookNowLink">
                {t('buttonsAndLinks.bookNow')}
              </Link>
            </Text>
          </div>
        </div>
      ) : null}

      <div
        className={cx(
          'border-t-4 border-surface pt-6',
          'md:max-w-[600px] md:flex-1 md:basis-3/5 md:border-l-2 md:border-t-0 md:pl-[6%] md:pt-0',
          {
            'md:flex-basis-full border-t-0 md:border-l-0': isAuthenticated,
          }
        )}
      >
        <picture>
          <img src="/dx_hiltonlink_app/static/images/login.png" alt="" />
        </picture>
        <Heading level={2} size="xl" className="my-4">
          {t('heading.welcomeTitle')}
        </Heading>
        {Array.isArray(signInPageContent) &&
          signInPageContent.map((content, idx) => (
            <Text
              className={cx('mb-4', { 'mb-0': signInPageContent.length === idx })}
              key={content.bodyText.slice(0, 50)}
            >
              {content.bodyText}
            </Text>
          ))}
        {isAuthenticated && (
          <>
            <Button
              as="Link"
              data-testid="continueBtn"
              fill="solid"
              size="lg"
              className="mt-2 w-full"
              label={t('buttonsAndLinks.continue')}
              url={`/${language}/my-event/group-search/${querystring ? `?${querystring}` : ``}`}
            />
            <Text className="my-5 border-t border-surface pt-4">
              {t('textContent.secureRoom')}{' '}
              <Link url={bookNowLink} data-testid="bookNowLink">
                {t('buttonsAndLinks.bookNow')}
              </Link>
            </Text>
          </>
        )}
      </div>
    </section>
  );
};

export default SignIn;
